import { useState, useCallback } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useEffects } from "../contexts/SettingsContext";
import client from "../api/client";

function RadarLogo() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
    >
      <circle cx="14" cy="14" r="3" fill="#5b7cff" />
      <circle
        cx="14"
        cy="14"
        r="7"
        stroke="#5b7cff"
        strokeWidth="1"
        strokeOpacity="0.5"
      />
      <circle
        cx="14"
        cy="14"
        r="11"
        stroke="#5b7cff"
        strokeWidth="1"
        strokeOpacity="0.3"
      />
      <circle
        cx="14"
        cy="14"
        r="13.5"
        stroke="#5b7cff"
        strokeWidth="0.5"
        strokeOpacity="0.15"
      />
    </svg>
  );
}

const NAV_LINKS = [
  { to: "/challenges", label: "Challenges" },
  { to: "/scoreboard", label: "Scoreboard" },
  { to: "/team", label: "Team" },
  { to: "/watch", label: "Watch" },
  { to: "/terminal", label: "Terminal" },
] as const;

export default function Navbar() {
  const { user, logout, loading } = useAuth();
  const { effects, setEffect } = useEffects();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const isAdmin = user?.role === "SUPER_ADMIN" || user?.role === "ORGANIZER";

  const toggleEffect = useCallback(async (key: "binaryRain" | "surveillance") => {
    const newVal = !effects[key];
    setEffect(key, newVal);
    try {
      await client.put("/admin/settings", {
        effects_binary_rain: key === "binaryRain" ? String(newVal) : String(effects.binaryRain),
        effects_surveillance: key === "surveillance" ? String(newVal) : String(effects.surveillance),
      });
    } catch {
      setEffect(key, !newVal);
    }
  }, [effects, setEffect]);

  const handleLogout = () => {
    setDropdownOpen(false);
    setMenuOpen(false);
    logout();
    navigate("/");
  };

  return (
    <nav className="fixed inset-x-0 top-0 z-50 border-b border-ds-border bg-ds-bg/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5">
          <RadarLogo />
          <span className="text-sm font-semibold text-ds-text">CyberSentinel</span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `relative px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "text-ds-accent"
                    : "text-ds-text-dim hover:text-ds-text"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {label}
                  {isActive && (
                    <span className="absolute inset-x-3 bottom-0 h-px bg-ds-accent" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-3 md:flex">
          {isAdmin && !loading && (
            <div className="flex items-center gap-1 border-r border-ds-border pr-3">
              <button
                onClick={() => toggleEffect("binaryRain")}
                className={`rounded p-1.5 text-xs transition-colors ${
                  effects.binaryRain
                    ? "text-ds-success bg-ds-success/10"
                    : "text-ds-text-muted hover:text-ds-text-dim"
                }`}
                title="Binary Rain"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M2 2h2v12H2V2zm4 2h2v10H6V4zm4-1h2v11h-2V3zm4 3h2v8h-2V6z" />
                </svg>
              </button>
              <button
                onClick={() => toggleEffect("surveillance")}
                className={`rounded p-1.5 text-xs transition-colors ${
                  effects.surveillance
                    ? "text-ds-success bg-ds-success/10"
                    : "text-ds-text-muted hover:text-ds-text-dim"
                }`}
                title="Surveillance Overlay"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="1" y="3" width="14" height="10" rx="1" />
                  <circle cx="8" cy="8" r="2.5" />
                  <circle cx="8" cy="8" r="0.8" fill="currentColor" />
                </svg>
              </button>
            </div>
          )}
          {loading ? (
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-ds-border border-t-ds-accent" />
          ) : user ? (
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-ds-accent">
                {user.totalPoints.toLocaleString()} pts
              </span>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-ds-text-dim transition-colors hover:bg-ds-surface hover:text-ds-text"
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-ds-accent/15 text-xs font-medium text-ds-accent">
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                  <span className="max-w-[100px] truncate">
                    {user.username}
                  </span>
                  <svg
                    className={`h-3.5 w-3.5 text-ds-text-muted transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {dropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setDropdownOpen(false)}
                    />
                    <div className="absolute right-0 z-50 mt-1 w-44 origin-top-right rounded-lg border border-ds-border bg-ds-card py-1 shadow-xl shadow-black/30">
                      <Link
                        to="/profile"
                        onClick={() => setDropdownOpen(false)}
                        className="block px-3 py-2 text-sm text-ds-text-dim transition-colors hover:bg-ds-surface hover:text-ds-text"
                      >
                        Profile
                      </Link>
                      {(user.role === "SUPER_ADMIN" ||
                        user.role === "ORGANIZER") && (
                        <Link
                          to="/admin"
                          onClick={() => setDropdownOpen(false)}
                          className="block px-3 py-2 text-sm text-ds-text-dim transition-colors hover:bg-ds-surface hover:text-ds-text"
                        >
                          Admin
                        </Link>
                      )}
                      <div className="my-1 border-t border-ds-border" />
                      <button
                        onClick={handleLogout}
                        className="block w-full px-3 py-2 text-left text-sm text-ds-coral transition-colors hover:bg-ds-coral/10"
                      >
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                className="rounded-md px-3 py-1.5 text-sm text-ds-text-dim transition-colors hover:text-ds-text"
              >
                Log in
              </Link>
              <Link
                to="/register"
                className="rounded-md bg-ds-accent px-3.5 py-1.5 text-sm font-medium text-white transition-colors hover:bg-ds-accent/90"
              >
                Register
              </Link>
            </div>
          )}
        </div>

        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="flex items-center justify-center rounded-md p-2 text-ds-text-dim transition-colors hover:text-ds-text md:hidden"
          aria-label="Toggle navigation menu"
        >
          {menuOpen ? (
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          )}
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-ds-border bg-ds-bg/95 backdrop-blur-md md:hidden">
          <div className="space-y-1 px-4 py-3">
            {NAV_LINKS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-ds-accent/10 text-ds-accent"
                      : "text-ds-text-dim hover:bg-ds-surface hover:text-ds-text"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </div>

          <div className="border-t border-ds-border px-4 py-3">
            {loading ? null : user ? (
              <div className="space-y-2">
                {isAdmin && (
                  <div className="flex items-center gap-2 px-3 py-1">
                    <button
                      onClick={() => toggleEffect("binaryRain")}
                      className={`rounded px-2 py-1 text-xs transition-colors ${
                        effects.binaryRain
                          ? "text-ds-success bg-ds-success/10"
                          : "text-ds-text-muted hover:text-ds-text-dim"
                      }`}
                    >
                      {effects.binaryRain ? "Matrix ON" : "Matrix OFF"}
                    </button>
                    <button
                      onClick={() => toggleEffect("surveillance")}
                      className={`rounded px-2 py-1 text-xs transition-colors ${
                        effects.surveillance
                          ? "text-ds-success bg-ds-success/10"
                          : "text-ds-text-muted hover:text-ds-text-dim"
                      }`}
                    >
                      {effects.surveillance ? "CCTV ON" : "CCTV OFF"}
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-3 px-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ds-accent/15 text-sm font-medium text-ds-accent">
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ds-text">
                      {user.username}
                    </p>
                    <p className="font-mono text-xs text-ds-accent">
                      {user.totalPoints.toLocaleString()} pts
                    </p>
                  </div>
                </div>
                <Link
                  to="/profile"
                  onClick={() => setMenuOpen(false)}
                  className="block rounded-md px-3 py-2 text-sm text-ds-text-dim transition-colors hover:bg-ds-surface hover:text-ds-text"
                >
                  Profile
                </Link>
                {(user.role === "SUPER_ADMIN" ||
                  user.role === "ORGANIZER") && (
                  <Link
                    to="/admin"
                    onClick={() => setMenuOpen(false)}
                    className="block rounded-md px-3 py-2 text-sm text-ds-text-dim transition-colors hover:bg-ds-surface hover:text-ds-text"
                  >
                    Admin
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="block w-full rounded-md px-3 py-2 text-left text-sm text-ds-coral transition-colors hover:bg-ds-coral/10"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="rounded-md px-3 py-2 text-center text-sm text-ds-text-dim transition-colors hover:bg-ds-surface hover:text-ds-text"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMenuOpen(false)}
                  className="rounded-md bg-ds-accent px-3.5 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-ds-accent/90"
                >
                  Register
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
